import { useTranslation } from 'react-i18next';
import PageHeader from '../components/PageHeader';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import {
  DirectionsBusTwoTone,
  FlightTwoTone,
  PlaceTwoTone,
  ScheduleTwoTone,
} from '@mui/icons-material';
import { AuthContext } from '../context/AuthContext';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { getCustomerTourPlanActivity } from '../api';
import { ITourPlanActivity } from '../utils/types';

export default function TourPlan({
  salesIndividualId,
  onEmptyDetails,
}: {
  salesIndividualId: number;
  onEmptyDetails?: (hasData: boolean) => void;
}) {
  const { t } = useTranslation();
  const { token } = useContext(AuthContext);
  const [tourPlanActivity, setTourPlanActivity] = useState<ITourPlanActivity>();

  const formattedDate = (date: string | Date): string => {
    return format(new Date(date), 'dd MMMM yyyy EEEE', { locale: tr });
  };

  let displayDayIndex = 0;

  useEffect(() => {
    fetchTourPlanActivity(salesIndividualId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesIndividualId]);

  const fetchTourPlanActivity = async (salesIndividualId: number) => {
    try {
      const res = await getCustomerTourPlanActivity(salesIndividualId, token);
      const data = await res.json();
      if (res?.status === 200) {
        setTourPlanActivity(data);
        if (!data.dateInfo) {
          onEmptyDetails?.(true);
        } else {
          onEmptyDetails?.(true);
        }
      } else {
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      onEmptyDetails?.(false);
      console.log('err');
    }
  };

  return (
    <>
      {tourPlanActivity && (
        <Box>
          <PageHeader title={t('tourPlan.title')} />
          <List>
            {tourPlanActivity?.dateInfo.map((day, index) => {
              displayDayIndex++;

              const hasPlans = day.trips.some((trip: any) =>
                trip.plans.some(
                  (plan: any) =>
                    !(
                      plan.accommodationCityName === null &&
                      plan.departureName === null &&
                      plan.arrivalName === null
                    ),
                ),
              );

              if (!hasPlans) return null;

              return (
                <List
                  key={index}
                  sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
                >
                  <Typography
                    color="primary"
                    variant="h6"
                    sx={{ fontWeight: 'bold' }}
                  >
                    {displayDayIndex}. Gün
                  </Typography>
                  <Typography color="primary" variant="h6">
                    {formattedDate(day.date)}
                  </Typography>
                  {day.trips.map((trip: any, tripIndex: number) =>
                    trip.plans.map((plan: any, planIndex: number) => (
                      <Box
                        key={`${tripIndex}-${planIndex}`}
                        sx={{
                          mt: 1,
                          ml: 2,
                          p: 1,
                          border: '1px solid #ddd',
                          borderRadius: 2,
                        }}
                      >
                        <ListItem>
                          <ListItemIcon>
                            <PlaceTwoTone />
                          </ListItemIcon>
                          {!plan.accommodationCityName ? (
                            <ListItemText
                              primary={`${plan?.departureName.join(
                                ',',
                              )} - ${plan?.arrivalName.join(',')}`}
                            />
                          ) : (
                            <ListItemText
                              primary={`${plan?.accommodationCityName}`}
                            />
                          )}
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <ScheduleTwoTone />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              plan?.time
                                ? plan.time.substring(0, 5)
                                : t('tourPlan.unknownTime')
                            }
                          />
                        </ListItem>
                        {plan.vehicleTypeCode && (
                          <ListItem>
                            <ListItemIcon>
                              {plan.vehicleTypeCode === 'PLN' ? (
                                <FlightTwoTone />
                              ) : (
                                <DirectionsBusTwoTone />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={plan?.transportationNumber}
                              secondary={
                                plan.vehicleTypeCode === 'PLN'
                                  ? plan.transportationCompany
                                  : ''
                              }
                            />
                          </ListItem>
                        )}
                      </Box>
                    )),
                  )}
                </List>
              );
            })}
          </List>
        </Box>
      )}
    </>
  );
}
